<template>
    <page :busy="busy" :tabs="tabs">
        <grid
            ref="grid"
            :defaultColDef="defaultColDef"
            :autoGroupColumnDef="autoGroupColumnDef"
            :columnDefs="columnDefs"
            :treeData="true"
            :isSearch="false"
            :getDataPath="getDataPath"
            :rowData="gridRowData"
            :pinnedBottomRowData="pinnedBottomRowData"
            :getRowClass="getRowClass"
            :getRowStyle="getRowStyle"
            rowSelection="multiple"
            :suppressRowClickSelection="true"
            :isRowSelectable="isRowSelectable"
            grid-theme="ag-theme-alpine-report"
            @selection-changed="handleRowSelect"
            :isGroupOpenByDefault="() => expanded"
        >
            <date-range-picker
                :range-options="dateRangePickerOptions"
                :value="datesValue"
                :date-format="userUnicodeDateFormat"
                @input="onDateRangePickerInput"
            ></date-range-picker>
            <responsive-dropdown
                :icon="['fas', 'filter']"
                :items="uncollectedOptions"
                variant="primary"
                >{{ uncollectedLabel }}</responsive-dropdown
            >
             <exportToPDFButton
                :href="exportPdfHref"
                :pdfCacheKey="pdfCacheKey"
                :pageOrientation="pageOrientation"
                tableFontSize="sm"
            />
            <responsive-dropdown
                :icon="['fas', 'file']"
                :items="orientationFileOption"
                variant="primary"
                >{{ orientationFileLabel }}
            </responsive-dropdown>
            <responsive-button
                :icon="['fas', 'arrows-rotate']"
                variant="primary"
                @click="onButtonRefresh"
                >Refresh</responsive-button
            >
            <responsive-button
                :icon="['fas', 'up-right-and-down-left-from-center']"
                variant="primary"
                @click="onButtonExpand"
            >
                <span v-if="expanded">Collapse</span>
                <span v-else>Expand</span>
            </responsive-button>
            <responsive-button
                :icon="['fas', 'bolt-lightning']"
                variant="primary"
                @click="isRowsSelected ? $bvModal.show(processSelectedModalId) : $bvModal.show(processSelectedInfoModalId)"
                >Process Selected</responsive-button
            >
        </grid>
        <modal
            :id="processSelectedModalId"
            title="Process Selected"
            @shown="onProcessSelectedModalShown"
            @ok="processSelected"
            ok-title="Process Selected"
            :ok-disabled="loading"
        >
            Are you sure you want to process selected subscribers?
        </modal>

        <busy-modal :show="loading"></busy-modal>

        <modal :id="processSelectedInfoModalId" title="Info" @hidden="processSelectedResponse = null">
            <div v-if="processSelectedResponse">
                <ul v-for="(item, inx) in processSelectedResponse" :key="item.transaction_code">
                    <strong>Receipt #{{ inx + 1 }}:</strong>
                    <li>Subscriber: {{ item.subscriber }}</li>
                    <li>Amount: {{ item.amount }}</li>
                    <li>Transaction Code: {{ item.transaction_code }}</li>
                    <li>Message: {{ item.message }}</li>
                </ul>
            </div>
            <span v-else>Nothing to process. Select subscribers.</span>
        </modal>

        <modal :id="processPaymentInfoModalId" title="Info" @hidden="processPaymentInfo = null">
            {{ processPaymentInfo }}
        </modal>
    </page>
</template>

<script>
    import { mapGetters } from 'vuex';
    import format from 'date-fns/format';
    import { Decimal } from 'decimal.js';
    import API from '../../../api';
    import Page from '../../generic/page/page.vue';
    import Grid from '../../generic/grid/grid.vue';
    import Modal from '../../generic/modal/modal.vue';
    import BusyModal from '../../generic/busy-modal/busy-modal.vue';
    import DateRangePicker, {
        nameToRange,
        TODAY,
        YESTERDAY,
        THIS_MONTH,
        LAST_MONTH,
        THIS_YEAR,
        LAST_YEAR,
    } from '../../generic/date-range-picker/date-range-picker.vue';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
    import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';
    import { formatResponseError } from '../../../utils/api';
    import { usdValueFormatter, valueFormatter } from '../../../utils/billing';
    import { 
        SERVER_DATE_FORMAT, 
        UNCOLLECTED_ALL, 
        UNCOLLECTED_UNCOLLECTED, 
        UNCOLLECTED, 
        PAGE_ORIENTATION, 
        PAGE_ORIENTATION_LABELS,
    } from '../../../constants';
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import { toAmountDisplay } from '../../../utils/numbers';
import { defineAsyncComponent } from 'vue';

    const UNCOLLECTED_ALL_LABEL = 'All Policies';
    const UNCOLLECTED_UNCOLLECTED_LABEL = 'Uncollected Only';
    const UNCOLLECTED_LABELS = [UNCOLLECTED_ALL_LABEL, UNCOLLECTED_UNCOLLECTED_LABEL];

    const exportToPDFButton = defineAsyncComponent(() =>
        import(
            /* webpackChunkName: "lx-export-to-pdf-button" */
            '../../generic/export-to-pdf-button/export-to-pdf-button.vue'
        ))

    const autoGroupValueFormatter = ({ value, data }) => {
        return data.subscriber_id ? `(${data.subscriber_id}) ${data.name}` : data.name;
    };

    export default {
        name: 'AdminIssuerBilling',
        components: {
            Page,
            Grid,
            Modal,
            BusyModal,
            DateRangePicker,
            ResponsiveButton,
            ResponsiveDropdown,
            exportToPDFButton,
            actionsCellRenderer: ActionsCellRenderer,
        },
        mixins: [removeScrollMixin],
        data() {
            const defaultUncollected =
                this.$store.getters['adminIssuerBillingLocalStorage/uncollected'];
            const defaultPageOrientation =
                this.$store.getters['adminIssuerBillingLocalStorage/pageOrientation'];
            const defaultExpanded =
                this.$store.getters['adminIssuerBillingLocalStorage/expanded'];
            return {
                busy: false,
                loading: false,
                uncollected: UNCOLLECTED[defaultUncollected],
                uncollectedLabel: UNCOLLECTED_LABELS[defaultUncollected],
                // Grid
                gridRowData: [],
                pinnedBottomRowData: [],
                defaultColDef: {
                    minWidth: 80,
                    sortable: false,
                },
                autoGroupColumnDef: {
                    flex: 2,
                    minWidth: 200,
                    resizable: true,
                    sortable: true,
                    sort: 'asc',
                    comparator: (valueA, valueB, nodeA, nodeB) => {
                        return nodeA.data.name > nodeB.data.name ? 1 : -1;
                    },
                    field: 'name',
                    valueFormatter: autoGroupValueFormatter,
                    pdfExportOptions: {
                        valueFormatter: autoGroupValueFormatter
                    },
                    headerName: 'Company / Policyholder',
                    cellStyle: { textAlign: 'center' },
                    cellRendererParams: {
                        checkbox: ({ data }) => !!data.subscriber_id,
                    },
                    headerCheckboxSelection: true,
                    headerCheckboxSelectionFilteredOnly: true,
                },
                getDataPath: (data) => data.id_hierarchy,
                gridContainerEl: null,
                gridApi: null,
                expanded: defaultExpanded,
                isRowSelectable: null,
                // Date-range Picker
                datesValue: nameToRange(LAST_MONTH),
                dateRangePickerOptions: [
                    TODAY,
                    YESTERDAY,
                    THIS_MONTH,
                    LAST_MONTH,
                    THIS_YEAR,
                    LAST_YEAR,
                ],
                total: null,
                pageOrientation: PAGE_ORIENTATION[defaultPageOrientation],
                orientationFileLabel: PAGE_ORIENTATION_LABELS[defaultPageOrientation],
                processSelectedModalId: 'modal-process-selected-id',
                processSelectedInfoModalId: 'modal-process-selected-info-id',
                processSelectedResponse: null,
                processPaymentInfoModalId: 'modal-process-info-id',
                processPaymentInfo: null,
                subscriberId: 0,
                isRowsSelected: false,
                pdfCacheKey: null,
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
            orientationFileOption() {
                return [
                    {
                        label: 'Landscape',
                        click: () => {
                            this.pageOrientation = PAGE_ORIENTATION[0];
                            this.orientationFileLabel = PAGE_ORIENTATION_LABELS[0];
                            this.$store.dispatch('adminIssuerBillingLocalStorage/changePageOrientation', 0);
                        },
                    },
                    {
                        label: 'Portrait',
                        click: () => {
                            this.pageOrientation = PAGE_ORIENTATION[1];
                            this.orientationFileLabel = PAGE_ORIENTATION_LABELS[1];
                            this.$store.dispatch('adminIssuerBillingLocalStorage/changePageOrientation', 1);
                        },
                    },
                  ];
            },
            title() {
                return 'Issuer Billing';
            },
            tabs() {
                return [
                    {
                        title: `${this.title} ${this.formattedDates}`,
                        route: '/app/admin/issuer-billing/',
                        active: true,
                    },
                ];
            },
            exportPdfHref() {
                return `/app/admin/issuer-billing/issuer-billing/pdf/?from_date=${format(
                    this.datesValue.from,
                    SERVER_DATE_FORMAT
                )}&to_date=${format(this.datesValue.to, SERVER_DATE_FORMAT)}&uncollected=${this.uncollected}&expanded=${
                    this.expanded
                }`;
            },
            columnDefs() {
                return [
                    {
                        field: 'name',
                        headerName: 'Company / Policyholder',
                        hide: true,
                    },
                    {
                        field: 'created',
                        headerName: 'Created',
                        type: 'dateColumn',
                        sortable: true,
                        width: 110,
                        minWidth: 110,
                    },
                    {
                        field: 'collect_status',
                        headerName: 'Collect Status',
                        sortable: true,
                        width: 100,
                        minWidth: 100,
                    },
                    {
                        field: 'collect_date',
                        headerName: 'Collect Date',
                        type: 'dateColumn',
                        sortable: true,
                        width: 110,
                        minWidth: 110,
                    },
                    {
                        field: 'transaction_code',
                        headerName: 'Transact. Code',
                        sortable: true,
                        width: 100,
                        minWidth: 100,
                    },
                    {
                        field: 'invoice_status',
                        headerName: 'Invoice Status',
                        sortable: true,
                        cellClass: ({ data }) => {
                            const wholesaleCost = Number(data.wholesale_cost);
                            if (!isNaN(wholesaleCost) && wholesaleCost < 0) {
                                return 'lx-cell-negative';
                            }
                            return '';
                        },
                        width: 110,
                        minWidth: 110,
                    },
                    {
                        field: 'invoice_no',
                        headerName: 'Invoice No.',
                        sortable: true,
                        width: 90,
                        minWidth: 90,
                    },
                    {
                        field: 'booking_total',
                        headerName: 'Booking Total',
                        headerClass: 'ag-right-aligned-header',
                        cellClass: 'ag-right-aligned-cell',
                        valueFormatter: valueFormatter,
                        pdfExportOptions: {
                            valueFormatter: valueFormatter
                        },
                        width: 110,
                        minWidth: 110,
                        sortable: true,
                        aggFunc: ({ data }) => {
                            return data && data.total_booking_total;
                        },
                    },
                    {
                        field: 'checkin',
                        headerName: 'Check-In',
                        type: 'dateColumn',
                        sortable: true,
                        width: 110,
                        minWidth: 110,
                    },
                    {
                        field: 'checkout',
                        headerName: 'Check-Out',
                        type: 'dateColumn',
                        sortable: true,
                        width: 110,
                        minWidth: 110,
                    },
                    {
                        field: 'policy_type',
                        headerName: 'Type',
                        sortable: true,
                        width: 100,
                        minWidth: 100,
                    },
                    {
                        field: 'wholesale_cost',
                        headerName: 'Wholesale',
                        headerClass: 'ag-right-aligned-header',
                        valueFormatter: usdValueFormatter,
                        cellClass: ({ value, data }) => {
                            const wholesaleCost = Number(value);
                            if (data.subscriber_id && wholesaleCost < 0) {
                                return 'ag-right-aligned-cell lx-cell-wrap';
                            }
                            if (!isNaN(wholesaleCost) && wholesaleCost < 0) {
                                return 'ag-right-aligned-cell lx-cell-negative';
                            }
                            return 'ag-right-aligned-cell';
                        },
                        width: 110,
                        minWidth: 110,
                        sortable: true,
                        aggFunc: ({ data }) => {
                            return data && data.total_wholesale_cost;
                        },
                    },
                    {
                        headerName: '',
                        minWidth: 155,
                        maxWidth: 155,
                        resizable: false,
                        cellStyle: { textAlign: 'center' },
                        cellRenderer: 'actionsCellRenderer',
                        cellRendererParams: {
                            onRecordPaymentSuccess: this.onRecordPaymentSuccess,
                            onProcessPaymentResponse: this.onProcessPaymentResponse,
                        },
                    },
                ];
            },
            formattedDates() {
                if (!this.datesValue.from || !this.datesValue.to) {
                    return '';
                }
                const formattedFrom = format(
                    this.datesValue.from,
                    this.userUnicodeDateFormat
                );
                const formattedTo = format(
                    this.datesValue.to,
                    this.userUnicodeDateFormat
                );
                return `${formattedFrom} - ${formattedTo}`;
            },
            uncollectedOptions() {
                return [
                    {
                        label: UNCOLLECTED_ALL_LABEL,
                        click: () => this.onUncollectedAll(),
                    },
                    {
                        label: UNCOLLECTED_UNCOLLECTED_LABEL,
                        click: () => this.onUncollectedUncollected(),
                    },
                ];
            },
        },
        beforeMount() {
            this.isRowSelectable = (rowNode) => {
                return !!rowNode.data.subscriber_id && !!rowNode.data.can_process_payment;
            };
        },
        mounted() {
            const grid = this.$refs.grid;
            this.gridContainerEl = grid.$refs.gridContainer;
            this.gridApi = grid.gridOptions.api;
            (async () => {
                await this.fetchGridRowData();
            })();
        },
        methods: {
            handleRowSelect() {
                this.isRowsSelected = !!this.gridApi.getSelectedRows().length;
            },
            async fetchGridRowData() {
                this.busy = true;
                try {
                    const response = await API.get(
                        '/app/admin/issuer-billing/issuer-billing/',
                        {
                            params: {
                                from_date: format(
                                    this.datesValue.from,
                                    SERVER_DATE_FORMAT
                                ),
                                to_date: format(
                                    this.datesValue.to,
                                    SERVER_DATE_FORMAT
                                ),
                                uncollected: this.uncollected,
                            },
                        }
                    );
                    this.pdfCacheKey = response.data.pdf_cache_key
                    this.gridRowData = response.data.items;
                    this.pinnedBottomRowData = [this.getPinnedBottomRowData(response.data.items)];
                 
                } catch (err) {
                    this.errorToast({ text: 'Could not load report data.' });
                    console.log(err);
                } finally {
                    this.busy = false;
                    this.isRowsSelected = false;
                    if (this.expanded) {
                        setTimeout(() => this.expandLevel0());
                    }
                }
            },
            getPinnedBottomRowData(rowData) {
                let wholesaleCost = new Decimal(0);
                let bookingTotal = new Decimal(0);
                for (let entry of rowData) {
                    if (!!entry.subscriber_id) {
                        const totalWholesaleCost = new Decimal(entry.total_wholesale_cost);
                        const totalBookingTotal = new Decimal(entry.total_booking_total);
                        if (!totalWholesaleCost.isNaN() && totalWholesaleCost.greaterThan(0)) {
                            wholesaleCost = wholesaleCost.plus(totalWholesaleCost);
                        }
                        if (!totalBookingTotal.isNaN() && totalBookingTotal.greaterThan(0)) {
                            bookingTotal = bookingTotal.plus(totalBookingTotal);
                        }
                    }
                }

                const bottomRow = {
                    wholesale_cost: wholesaleCost.toNumber(),
                    booking_total: bookingTotal.toNumber(),
                }
                this.total = bottomRow;
                return bottomRow;
            },
            expandLevel0() {
                this.gridApi.collapseAll();
                this.gridApi.forEachNode((node) => {
                    if (node.level === 0) {
                        node.setExpanded(true);
                    }
                });
                this.expanded = true;
                this.$store.dispatch('adminIssuerBillingLocalStorage/changeExpanded', true);
            },
            async onDateRangePickerInput(dates) {
                this.datesValue = { ...dates };
                await this.fetchGridRowData();
            },
            async onUncollectedAll() {
                if (this.uncollected === UNCOLLECTED_ALL) {
                    return;
                }
                this.uncollected = UNCOLLECTED_ALL;
                this.uncollectedLabel = UNCOLLECTED_ALL_LABEL;
                this.$store.dispatch(
                    'adminIssuerBillingLocalStorage/changeUncollected',
                    UNCOLLECTED.indexOf(UNCOLLECTED_ALL)
                );
                await this.fetchGridRowData();
            },
            async onUncollectedUncollected() {
                if (this.uncollected === UNCOLLECTED_UNCOLLECTED) {
                    return;
                }
                this.uncollected = UNCOLLECTED_UNCOLLECTED;
                this.uncollectedLabel = UNCOLLECTED_UNCOLLECTED_LABEL;
                this.$store.dispatch(
                    'adminIssuerBillingLocalStorage/changeUncollected',
                    UNCOLLECTED.indexOf(UNCOLLECTED_UNCOLLECTED)
                );
                await this.fetchGridRowData();
            },
            async onButtonRefresh() {
                await this.fetchGridRowData();
            },
            async processSelected() {
                this.loading = true;
                try {
                    let response = await API.post(
                        '/app/admin/issuer-billing/issuer-billing/bulk-process-payment/',
                        { items: this.processSelectedItems }
                    );
                    this.$bvModal.hide(this.processSelectedModalId);
                    this.processSelectedResponse = response.data;
                    await this.fetchGridRowData();
                    this.$bvModal.show(this.processSelectedInfoModalId);
                } catch (err) {
                    const { generic, fields } = formatResponseError(err);
                    this.errorToast({ text: generic || 'Could not process selected.' });
                } finally {
                    this.loading = false;
                }
            },
            onProcessSelectedModalShown() {
                this.processSelectedItems = []
                let rows = this.gridApi.getSelectedRows();
                rows.forEach(row => {
                    this.processSelectedItems.push({subscriber_id: row.subscriber_id, policy_ids: row.policy_ids});
                });
            },
            onButtonExpand() {
                if (this.expanded) {
                    this.gridApi.collapseAll();
                    this.expanded = false;
                    this.$store.dispatch('adminIssuerBillingLocalStorage/changeExpanded', false);
                } else {
                    this.expandLevel0();
                }
            },
            onRecordPaymentSuccess() {
                this.successToast({ text: 'Payment recorded.' });
                this.fetchGridRowData();
            },
            onProcessPaymentResponse({ message }) {
                this.processPaymentInfo = message.message;
                this.$bvModal.show(this.processPaymentInfoModalId);
                this.fetchGridRowData();
            },
            getRowClass({ data }) {
                if (!data.created) {
                    return 'lx-row-group';
                }
                return '';
            },
            getRowStyle({ data, node }) {
                if (!data.created) {
                    node.pdfRowStyle = {
                        bold: true,
                    };
                    if (node.rowIndex !== 0) {
                        node.pdfRowStyle.margin = [0, 15, 0, 0];
                    }
                }
            }
        },
    };
</script>
